import { CRM_LEAD_SOURCES } from "@/lib/crmDisplay";

export type CrmLeadFieldSectionId = "lead_status" | "account_lead" | "contact_lead";

export type CrmLeadFieldKind =
  | "text"
  | "select"
  | "email_type"
  | "phone_type"
  | "phone_country_default"
  | "address_type"
  | "address_country_default"
  | "social_type";

export type CrmLeadFieldSelectOption = {
  value: string;
  label: string;
  /** When true, omitted from New Lead pickers unless already selected on a record. */
  hidden?: boolean;
  /** For dependent selects (e.g. Industry segment): parent Industry option value. */
  parentOptionValue?: string;
};

export type CrmLeadFieldDefinition = {
  id: string;
  label: string;
  kind: CrmLeadFieldKind;
  required?: boolean;
  placeholder?: string;
  optionValue?: string;
  optionLabel?: string;
  /** Public object URL for social_type option icon (org tenant assets). */
  optionIconUrl?: string;
  selectOptions?: CrmLeadFieldSelectOption[];
  isStandard?: boolean;
  /** When true, field (or channel option row) is hidden on New Lead create forms. */
  hidden?: boolean;
};

export type CrmLeadModuleFieldsDocument = {
  entityType: "CrmLeadModuleFields";
  orgId: string;
  sections: Record<CrmLeadFieldSectionId, CrmLeadFieldDefinition[]>;
  updatedAt: string;
  updatedBy?: string | null;
};

export const CRM_LEAD_FIELD_SECTION_LABELS: Record<CrmLeadFieldSectionId, string> = {
  lead_status: "Lead status",
  account_lead: "Account Lead",
  contact_lead: "Contact Lead",
};

export const CRM_LEAD_FIELD_SECTION_IDS: CrmLeadFieldSectionId[] = [
  "lead_status",
  "account_lead",
  "contact_lead",
];

export type CrmLeadChannelFieldGroupId = "email" | "phone" | "address" | "social";

export const CRM_LEAD_CHANNEL_FIELD_GROUP_IDS: CrmLeadChannelFieldGroupId[] = [
  "email",
  "phone",
  "address",
  "social",
];

export const CRM_LEAD_CHANNEL_FIELD_GROUP_LABELS: Record<CrmLeadChannelFieldGroupId, string> = {
  email: "Email",
  phone: "Phone",
  address: "Address",
  social: "Social",
};

export const CRM_LEAD_FIELD_SECTION_SLUGS: Record<CrmLeadFieldSectionId, string> = {
  lead_status: "lead-status",
  account_lead: "account-lead",
  contact_lead: "contact-lead",
};

const SECTION_SLUG_TO_ID: Record<string, CrmLeadFieldSectionId> = {
  "lead-status": "lead_status",
  "account-lead": "account_lead",
  "contact-lead": "contact_lead",
};

export function crmLeadFieldSectionIdFromSlug(slug: string): CrmLeadFieldSectionId | null {
  return SECTION_SLUG_TO_ID[slug.toLowerCase()] ?? null;
}

export function crmLeadFieldSectionSlug(sectionId: CrmLeadFieldSectionId): string {
  return CRM_LEAD_FIELD_SECTION_SLUGS[sectionId];
}

export function crmLeadFieldDetailPath(
  sectionId: CrmLeadFieldSectionId,
  groupId: CrmLeadChannelFieldGroupId,
): string {
  return `/crm/settings/modules/lead/fields/${crmLeadFieldSectionSlug(sectionId)}/${groupId}`;
}

export function isCrmLeadChannelDetailKind(kind: CrmLeadFieldKind): boolean {
  return (
    kind === "email_type" ||
    kind === "phone_type" ||
    kind === "phone_country_default" ||
    kind === "address_type" ||
    kind === "address_country_default" ||
    kind === "social_type"
  );
}

export function crmLeadChannelDetailKinds(groupId: CrmLeadChannelFieldGroupId): CrmLeadFieldKind[] {
  if (groupId === "email") return ["email_type"];
  if (groupId === "phone") return ["phone_country_default", "phone_type"];
  if (groupId === "address") return ["address_country_default", "address_type"];
  return ["social_type"];
}

export function crmLeadFieldBelongsToChannelGroup(
  field: CrmLeadFieldDefinition,
  groupId: CrmLeadChannelFieldGroupId,
): boolean {
  return crmLeadChannelDetailKinds(groupId).includes(field.kind);
}

/** Fields shown on the section list (hides channel option rows). */
export function crmLeadFieldsForSectionList(
  sectionId: CrmLeadFieldSectionId,
  fields: CrmLeadFieldDefinition[],
): CrmLeadFieldDefinition[] {
  if (sectionId === "lead_status") return fields;
  return fields.filter((f) => !isCrmLeadChannelDetailKind(f.kind));
}

export function crmLeadFieldIsHidden(field: CrmLeadFieldDefinition): boolean {
  return field.hidden === true;
}

/** Select options offered on create forms; keeps `currentValue` visible when hidden. */
export function crmLeadSelectOptionsForForm(
  options: CrmLeadFieldSelectOption[] | undefined,
  currentValue?: string | null,
): CrmLeadFieldSelectOption[] {
  if (!options?.length) return [];
  const visible = options.filter((o) => !o.hidden);
  const cur = currentValue != null ? String(currentValue).trim() : "";
  if (cur && !visible.some((o) => o.value === cur)) {
    const match = options.find((o) => o.value === cur);
    if (match) return [...visible, match];
    return [...visible, { value: cur, label: cur }];
  }
  return visible;
}

/** Select options filtered by parent Industry value; keeps current segment visible when hidden or custom. */
export function crmLeadSelectOptionsForParentForm(
  options: CrmLeadFieldSelectOption[] | undefined,
  parentValue: string | null | undefined,
  currentValue?: string | null,
): CrmLeadFieldSelectOption[] {
  const parent = parentValue != null ? String(parentValue).trim() : "";
  if (!parent) return [];
  const forParent = (options ?? []).filter((o) => o.parentOptionValue === parent);
  return crmLeadSelectOptionsForForm(forParent, currentValue);
}

/** Map legacy free-text industry to a standard option value when label/value matches. */
export function crmLeadIndustryValueFromLegacyText(
  raw: string | null | undefined,
  industryOptions: CrmLeadFieldSelectOption[] | undefined,
): string {
  const text = raw != null ? String(raw).trim() : "";
  if (!text || !industryOptions?.length) return text;
  const byValue = industryOptions.find((o) => o.value === text);
  if (byValue) return byValue.value;
  const lower = text.toLowerCase();
  const byLabel = industryOptions.find((o) => o.label.trim().toLowerCase() === lower);
  if (byLabel) return byLabel.value;
  return text;
}

export function crmLeadSelectOptionLabel(
  options: CrmLeadFieldSelectOption[] | undefined,
  value: string | null | undefined,
): string {
  const v = value != null ? String(value).trim() : "";
  if (!v) return "";
  const match = options?.find((o) => o.value === v);
  return match?.label ?? v;
}

export function crmLeadStatusFieldById(
  sections: CrmLeadModuleFieldsDocument["sections"] | null | undefined,
  fieldId: "source" | "stage" | "status",
): CrmLeadFieldDefinition | null {
  const fields = sections?.lead_status?.length
    ? sections.lead_status
    : seedStandardCrmLeadModuleFields().lead_status;
  return fields.find((f) => f.id === fieldId) ?? null;
}

export function crmLeadSectionFieldById(
  sections: CrmLeadModuleFieldsDocument["sections"] | null | undefined,
  sectionId: CrmLeadFieldSectionId,
  fieldId: string,
): CrmLeadFieldDefinition | null {
  const fields = sections?.[sectionId]?.length
    ? sections[sectionId]
    : seedStandardCrmLeadModuleFields()[sectionId];
  return fields.find((f) => f.id === fieldId) ?? null;
}

const ACCOUNT_LEAD_LEGACY_LOCATION_FIELD_IDS = new Set(["city", "state"]);

export function crmLeadFieldVisibleOnNewLead(
  sections: CrmLeadModuleFieldsDocument["sections"] | null | undefined,
  sectionId: CrmLeadFieldSectionId,
  fieldId: string,
): boolean {
  if (sectionId === "account_lead" && ACCOUNT_LEAD_LEGACY_LOCATION_FIELD_IDS.has(fieldId)) {
    return false;
  }
  const field = crmLeadSectionFieldById(sections, sectionId, fieldId);
  if (!field) return true;
  return !crmLeadFieldIsHidden(field);
}

export function sectionHasChannelFieldGroups(sectionId: CrmLeadFieldSectionId): boolean {
  return sectionId === "account_lead" || sectionId === "contact_lead";
}

const LEAD_STAGES = [
  { value: "new", label: "New" },
  { value: "qualified", label: "Qualified" },
  { value: "meeting_booked", label: "Meeting booked" },
  { value: "proposal_sent", label: "Proposal sent" },
  { value: "negotiation", label: "Negotiation" },
];

const LEAD_STATUSES = [
  { value: "open", label: "Open" },
  { value: "working", label: "Working" },
  { value: "paused", label: "Paused" },
];

const STANDARD_INDUSTRY_SELECT_OPTIONS: CrmLeadFieldSelectOption[] = [
  { value: "technology", label: "Technology" },
  { value: "professional_services", label: "Professional Services" },
  { value: "healthcare", label: "Healthcare" },
  { value: "financial_services", label: "Financial Services" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "retail_ecommerce", label: "Retail & E-commerce" },
  { value: "education", label: "Education" },
  { value: "government_nonprofit", label: "Government & Nonprofit" },
  { value: "real_estate_construction", label: "Real Estate & Construction" },
  { value: "energy_utilities", label: "Energy & Utilities" },
  { value: "media_entertainment", label: "Media & Entertainment" },
  { value: "other", label: "Other" },
];

const STANDARD_INDUSTRY_SEGMENT_SELECT_OPTIONS: CrmLeadFieldSelectOption[] = [
  { value: "software_saas", label: "Software / SaaS", parentOptionValue: "technology" },
  { value: "it_services", label: "IT Services", parentOptionValue: "technology" },
  { value: "hardware", label: "Hardware", parentOptionValue: "technology" },
  { value: "telecommunications", label: "Telecommunications", parentOptionValue: "technology" },
  { value: "consulting", label: "Consulting", parentOptionValue: "professional_services" },
  { value: "legal", label: "Legal", parentOptionValue: "professional_services" },
  { value: "accounting", label: "Accounting", parentOptionValue: "professional_services" },
  {
    value: "marketing_advertising",
    label: "Marketing & Advertising",
    parentOptionValue: "professional_services",
  },
  { value: "hospitals_clinics", label: "Hospitals & Clinics", parentOptionValue: "healthcare" },
  { value: "health_tech", label: "Health Tech", parentOptionValue: "healthcare" },
  { value: "pharma_biotech", label: "Pharma & Biotech", parentOptionValue: "healthcare" },
  { value: "banking", label: "Banking", parentOptionValue: "financial_services" },
  { value: "insurance", label: "Insurance", parentOptionValue: "financial_services" },
  { value: "fintech", label: "Fintech", parentOptionValue: "financial_services" },
  { value: "capital_markets", label: "Capital Markets", parentOptionValue: "financial_services" },
  { value: "industrial", label: "Industrial", parentOptionValue: "manufacturing" },
  { value: "consumer_goods", label: "Consumer Goods", parentOptionValue: "manufacturing" },
  { value: "automotive", label: "Automotive", parentOptionValue: "manufacturing" },
  { value: "retail", label: "Retail", parentOptionValue: "retail_ecommerce" },
  { value: "ecommerce", label: "E-commerce", parentOptionValue: "retail_ecommerce" },
  { value: "wholesale", label: "Wholesale", parentOptionValue: "retail_ecommerce" },
  { value: "k12", label: "K-12", parentOptionValue: "education" },
  { value: "higher_education", label: "Higher Education", parentOptionValue: "education" },
  { value: "edtech", label: "EdTech", parentOptionValue: "education" },
  { value: "government", label: "Government", parentOptionValue: "government_nonprofit" },
  { value: "nonprofit", label: "Nonprofit", parentOptionValue: "government_nonprofit" },
  { value: "real_estate", label: "Real Estate", parentOptionValue: "real_estate_construction" },
  { value: "construction", label: "Construction", parentOptionValue: "real_estate_construction" },
  { value: "energy", label: "Energy", parentOptionValue: "energy_utilities" },
  { value: "utilities", label: "Utilities", parentOptionValue: "energy_utilities" },
  { value: "media", label: "Media", parentOptionValue: "media_entertainment" },
  { value: "entertainment", label: "Entertainment", parentOptionValue: "media_entertainment" },
  { value: "general", label: "General", parentOptionValue: "other" },
  { value: "other_segment", label: "Other", parentOptionValue: "other" },
];

export function standardIndustrySelectOptions(): CrmLeadFieldSelectOption[] {
  return STANDARD_INDUSTRY_SELECT_OPTIONS.map((o) => ({ ...o }));
}

export function standardIndustrySegmentSelectOptions(): CrmLeadFieldSelectOption[] {
  return STANDARD_INDUSTRY_SEGMENT_SELECT_OPTIONS.map((o) => ({ ...o }));
}

export function seedStandardCrmLeadModuleFields(): Record<
  CrmLeadFieldSectionId,
  CrmLeadFieldDefinition[]
> {
  return {
    lead_status: [
      {
        id: "source",
        label: "Source",
        kind: "select",
        isStandard: true,
        selectOptions: CRM_LEAD_SOURCES.map((s) => ({ value: s, label: s })),
      },
      {
        id: "stage",
        label: "Stage",
        kind: "select",
        isStandard: true,
        selectOptions: LEAD_STAGES,
      },
      {
        id: "status",
        label: "Status",
        kind: "select",
        isStandard: true,
        selectOptions: LEAD_STATUSES,
      },
    ],
    account_lead: [
      { id: "company_name", label: "Company name", kind: "text", required: true, isStandard: true },
      { id: "domain", label: "Domain", kind: "text", isStandard: true, placeholder: "company.com" },
      { id: "website", label: "Website", kind: "text", isStandard: true },
      {
        id: "city",
        label: "City",
        kind: "text",
        isStandard: true,
        hidden: true,
      },
      {
        id: "state",
        label: "State",
        kind: "text",
        isStandard: true,
        hidden: true,
      },
      { id: "industry", label: "Industry", kind: "select", isStandard: true, selectOptions: standardIndustrySelectOptions() },
      {
        id: "industry_segment",
        label: "Industry segment",
        kind: "select",
        isStandard: true,
        selectOptions: standardIndustrySegmentSelectOptions(),
      },
      {
        id: "email_type_company",
        label: "Company",
        kind: "email_type",
        optionValue: "company",
        optionLabel: "Company",
        isStandard: true,
      },
      {
        id: "email_type_business",
        label: "Business",
        kind: "email_type",
        optionValue: "business",
        optionLabel: "Business",
        isStandard: true,
      },
      {
        id: "email_type_personal",
        label: "Personal",
        kind: "email_type",
        optionValue: "personal",
        optionLabel: "Personal",
        isStandard: true,
      },
      {
        id: "phone_default_country",
        label: "Standard country code",
        kind: "phone_country_default",
        optionValue: "us",
        optionLabel: "United States (+1)",
        isStandard: true,
      },
      {
        id: "phone_type_mobile",
        label: "Mobile",
        kind: "phone_type",
        optionValue: "mobile",
        optionLabel: "Mobile",
        isStandard: true,
      },
      {
        id: "phone_type_business",
        label: "Business",
        kind: "phone_type",
        optionValue: "business",
        optionLabel: "Business",
        isStandard: true,
      },
      {
        id: "phone_type_home",
        label: "Personal",
        kind: "phone_type",
        optionValue: "home",
        optionLabel: "Personal",
        isStandard: true,
      },
      {
        id: "address_default_country",
        label: "Standard country",
        kind: "address_country_default",
        optionValue: "us",
        optionLabel: "United States of America (USA)",
        isStandard: true,
      },
      {
        id: "address_type_billing",
        label: "Billing",
        kind: "address_type",
        optionValue: "billing",
        optionLabel: "Billing",
        isStandard: true,
      },
      {
        id: "address_type_shipping",
        label: "Shipping",
        kind: "address_type",
        optionValue: "shipping",
        optionLabel: "Shipping",
        isStandard: true,
      },
      {
        id: "address_type_headquarters",
        label: "Headquarters",
        kind: "address_type",
        optionValue: "headquarters",
        optionLabel: "Headquarters",
        isStandard: true,
      },
      {
        id: "address_type_home",
        label: "Home",
        kind: "address_type",
        optionValue: "home",
        optionLabel: "Home",
        isStandard: true,
      },
      {
        id: "social_linkedin",
        label: "LinkedIn",
        kind: "social_type",
        optionValue: "linkedin",
        optionLabel: "LinkedIn",
        isStandard: true,
      },
      {
        id: "social_facebook",
        label: "Facebook",
        kind: "social_type",
        optionValue: "facebook",
        optionLabel: "Facebook",
        isStandard: true,
      },
      {
        id: "social_twitter",
        label: "Twitter / X",
        kind: "social_type",
        optionValue: "twitter",
        optionLabel: "Twitter / X",
        isStandard: true,
      },
      {
        id: "social_instagram",
        label: "Instagram",
        kind: "social_type",
        optionValue: "instagram",
        optionLabel: "Instagram",
        isStandard: true,
      },
      {
        id: "social_youtube",
        label: "YouTube",
        kind: "social_type",
        optionValue: "youtube",
        optionLabel: "YouTube",
        isStandard: true,
      },
      {
        id: "social_github",
        label: "GitHub",
        kind: "social_type",
        optionValue: "github",
        optionLabel: "GitHub",
        isStandard: true,
      },
      {
        id: "social_website",
        label: "Website",
        kind: "social_type",
        optionValue: "website",
        optionLabel: "Website",
        isStandard: true,
      },
    ],
    contact_lead: [
      { id: "first_name", label: "First name", kind: "text", isStandard: true },
      { id: "last_name", label: "Last name", kind: "text", isStandard: true },
      { id: "title", label: "Title", kind: "text", isStandard: true },
      {
        id: "email_type_business",
        label: "Business",
        kind: "email_type",
        optionValue: "business",
        optionLabel: "Business",
        isStandard: true,
      },
      {
        id: "email_type_personal",
        label: "Personal",
        kind: "email_type",
        optionValue: "personal",
        optionLabel: "Personal",
        isStandard: true,
      },
      {
        id: "phone_default_country",
        label: "Standard country code",
        kind: "phone_country_default",
        optionValue: "us",
        optionLabel: "United States (+1)",
        isStandard: true,
      },
      {
        id: "phone_type_mobile",
        label: "Mobile",
        kind: "phone_type",
        optionValue: "mobile",
        optionLabel: "Mobile",
        isStandard: true,
      },
      {
        id: "phone_type_business",
        label: "Business",
        kind: "phone_type",
        optionValue: "business",
        optionLabel: "Business",
        isStandard: true,
      },
      {
        id: "phone_type_home",
        label: "Personal",
        kind: "phone_type",
        optionValue: "home",
        optionLabel: "Personal",
        isStandard: true,
      },
      {
        id: "address_default_country",
        label: "Standard country",
        kind: "address_country_default",
        optionValue: "us",
        optionLabel: "United States of America (USA)",
        isStandard: true,
      },
      {
        id: "address_type_billing",
        label: "Billing",
        kind: "address_type",
        optionValue: "billing",
        optionLabel: "Billing",
        isStandard: true,
      },
      {
        id: "address_type_shipping",
        label: "Shipping",
        kind: "address_type",
        optionValue: "shipping",
        optionLabel: "Shipping",
        isStandard: true,
      },
      {
        id: "address_type_headquarters",
        label: "Headquarters",
        kind: "address_type",
        optionValue: "headquarters",
        optionLabel: "Headquarters",
        isStandard: true,
      },
      {
        id: "address_type_home",
        label: "Home",
        kind: "address_type",
        optionValue: "home",
        optionLabel: "Home",
        isStandard: true,
      },
      {
        id: "social_linkedin",
        label: "LinkedIn",
        kind: "social_type",
        optionValue: "linkedin",
        optionLabel: "LinkedIn",
        isStandard: true,
      },
      {
        id: "social_facebook",
        label: "Facebook",
        kind: "social_type",
        optionValue: "facebook",
        optionLabel: "Facebook",
        isStandard: true,
      },
      {
        id: "social_twitter",
        label: "Twitter / X",
        kind: "social_type",
        optionValue: "twitter",
        optionLabel: "Twitter / X",
        isStandard: true,
      },
      {
        id: "social_instagram",
        label: "Instagram",
        kind: "social_type",
        optionValue: "instagram",
        optionLabel: "Instagram",
        isStandard: true,
      },
      {
        id: "social_youtube",
        label: "YouTube",
        kind: "social_type",
        optionValue: "youtube",
        optionLabel: "YouTube",
        isStandard: true,
      },
      {
        id: "social_github",
        label: "GitHub",
        kind: "social_type",
        optionValue: "github",
        optionLabel: "GitHub",
        isStandard: true,
      },
      {
        id: "social_website",
        label: "Website",
        kind: "social_type",
        optionValue: "website",
        optionLabel: "Website",
        isStandard: true,
      },
    ],
  };
}

export function defaultCrmLeadModuleFieldsDocument(orgId: string): CrmLeadModuleFieldsDocument {
  const ts = new Date().toISOString();
  return {
    entityType: "CrmLeadModuleFields",
    orgId,
    sections: seedStandardCrmLeadModuleFields(),
    updatedAt: ts,
  };
}

/** Insert missing standard Address channel rows without reordering saved fields. */
export function mergeMissingStandardAddressChannelFields(
  sectionId: CrmLeadFieldSectionId,
  fields: CrmLeadFieldDefinition[],
): CrmLeadFieldDefinition[] {
  if (sectionId === "lead_status") return fields;

  const standard = seedStandardCrmLeadModuleFields()[sectionId];
  const addressStandards = standard.filter(
    (f) => f.kind === "address_country_default" || f.kind === "address_type",
  );
  const present = new Set(fields.map((f) => f.id));
  const missing = addressStandards.filter((f) => !present.has(f.id));
  if (!missing.length) return fields;

  let insertAt = fields.length;
  const socialIdx = fields.findIndex((f) => f.kind === "social_type");
  if (socialIdx >= 0) {
    insertAt = socialIdx;
  } else {
    for (let i = 0; i < fields.length; i++) {
      const k = fields[i]!.kind;
      if (k === "phone_country_default" || k === "phone_type") insertAt = i + 1;
    }
  }

  return [...fields.slice(0, insertAt), ...missing, ...fields.slice(insertAt)];
}

function crmLeadFieldSelectNeedsStandardOptions(
  field: CrmLeadFieldDefinition,
  standardOptions: CrmLeadFieldSelectOption[],
): boolean {
  if (field.kind === "text") return true;
  if (field.kind !== "select") return false;
  if (!field.selectOptions?.length) return true;
  if (!standardOptions.length) return false;
  return crmLeadSelectOptionsForForm(field.selectOptions).length === 0;
}

/** Ensures Industry select + Industry segment exist; upgrades legacy text Industry to select seed. */
export function mergeMissingStandardAccountLeadIndustryFields(
  fields: CrmLeadFieldDefinition[],
): CrmLeadFieldDefinition[] {
  const standard = seedStandardCrmLeadModuleFields().account_lead;
  const industryStandard = standard.find((f) => f.id === "industry");
  const segmentStandard = standard.find((f) => f.id === "industry_segment");
  let next = [...fields];

  const industryIdx = next.findIndex((f) => f.id === "industry");
  if (industryIdx >= 0 && industryStandard) {
    const current = next[industryIdx]!;
    if (
      crmLeadFieldSelectNeedsStandardOptions(
        current,
        industryStandard.selectOptions ?? [],
      )
    ) {
      next[industryIdx] = {
        ...industryStandard,
        label: current.label || industryStandard.label,
        required: current.required,
        hidden: current.hidden,
        placeholder: current.placeholder,
      };
    }
  } else if (industryStandard) {
    const websiteIdx = next.findIndex((f) => f.id === "website");
    const insertAt = websiteIdx >= 0 ? websiteIdx + 1 : next.length;
    next = [...next.slice(0, insertAt), industryStandard, ...next.slice(insertAt)];
  }

  const segmentIdx = next.findIndex((f) => f.id === "industry_segment");
  if (segmentIdx >= 0 && segmentStandard) {
    const current = next[segmentIdx]!;
    if (
      crmLeadFieldSelectNeedsStandardOptions(
        current,
        segmentStandard.selectOptions ?? [],
      )
    ) {
      next[segmentIdx] = {
        ...segmentStandard,
        label: current.label || segmentStandard.label,
        required: current.required,
        hidden: current.hidden,
        placeholder: current.placeholder,
      };
    }
  } else if (segmentStandard) {
    const industryAfterIdx = next.findIndex((f) => f.id === "industry");
    const insertAt = industryAfterIdx >= 0 ? industryAfterIdx + 1 : next.length;
    next = [...next.slice(0, insertAt), segmentStandard, ...next.slice(insertAt)];
  }

  return next;
}

/** Apply GET-time merges in the browser so forms always see standard Industry options. */
export function mergeStandardCrmLeadModuleFieldSections(
  sections: CrmLeadModuleFieldsDocument["sections"],
): CrmLeadModuleFieldsDocument["sections"] {
  const seed = seedStandardCrmLeadModuleFields();
  const base = sections ?? seed;
  return {
    lead_status: base.lead_status?.length ? base.lead_status : seed.lead_status,
    account_lead: mergeMissingStandardAccountLeadIndustryFields(
      mergeMissingStandardAddressChannelFields("account_lead", base.account_lead ?? []),
    ),
    contact_lead: mergeMissingStandardAddressChannelFields(
      "contact_lead",
      base.contact_lead ?? [],
    ),
  };
}

function normalizeField(raw: unknown): CrmLeadFieldDefinition | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const id = String(row.id || "").trim();
  const label = String(row.label || "").trim();
  const kind = String(row.kind || "").trim() as CrmLeadFieldKind;
  if (!id || !label) return null;
  if (
    kind !== "text" &&
    kind !== "select" &&
    kind !== "email_type" &&
    kind !== "phone_type" &&
    kind !== "phone_country_default" &&
    kind !== "address_type" &&
    kind !== "address_country_default" &&
    kind !== "social_type"
  ) {
    return null;
  }
  return {
    id,
    label,
    kind,
    required: row.required === true,
    placeholder: row.placeholder != null ? String(row.placeholder) : undefined,
    optionValue: row.optionValue != null ? String(row.optionValue) : undefined,
    optionLabel: row.optionLabel != null ? String(row.optionLabel) : undefined,
    optionIconUrl:
      row.optionIconUrl != null && String(row.optionIconUrl).trim()
        ? String(row.optionIconUrl).trim()
        : undefined,
    selectOptions: Array.isArray(row.selectOptions)
      ? row.selectOptions
          .map((o) => {
            if (!o || typeof o !== "object") return null;
            const opt = o as Record<string, unknown>;
            const value = String(opt.value ?? "").trim();
            const optLabel = String(opt.label ?? value).trim();
            if (!value) return null;
            const option: CrmLeadFieldSelectOption = {
              value,
              label: optLabel || value,
            };
            if (opt.hidden === true) option.hidden = true;
            if (opt.parentOptionValue != null && String(opt.parentOptionValue).trim()) {
              option.parentOptionValue = String(opt.parentOptionValue).trim();
            }
            return option;
          })
          .filter((o): o is CrmLeadFieldSelectOption => o != null)
      : undefined,
    isStandard: row.isStandard === true,
    hidden: row.hidden === true ? true : undefined,
  };
}

export function normalizeCrmLeadModuleFieldsDocument(
  orgId: string,
  raw: unknown,
): CrmLeadModuleFieldsDocument {
  const base = defaultCrmLeadModuleFieldsDocument(orgId);
  if (!raw || typeof raw !== "object") return base;
  const row = raw as Record<string, unknown>;
  const sectionsRaw = row.sections;
  if (!sectionsRaw || typeof sectionsRaw !== "object") return base;

  const next: Record<CrmLeadFieldSectionId, CrmLeadFieldDefinition[]> = { ...base.sections };
  for (const sectionId of CRM_LEAD_FIELD_SECTION_IDS) {
    const list = (sectionsRaw as Record<string, unknown>)[sectionId];
    if (!Array.isArray(list)) continue;
    const normalized = list.map(normalizeField).filter((f): f is CrmLeadFieldDefinition => Boolean(f));
    if (normalized.length) {
      let merged = mergeMissingStandardAddressChannelFields(sectionId, normalized);
      if (sectionId === "account_lead") {
        merged = mergeMissingStandardAccountLeadIndustryFields(merged);
      }
      next[sectionId] = merged;
    }
  }

  return {
    ...base,
    sections: next,
    updatedAt: typeof row.updatedAt === "string" ? row.updatedAt : base.updatedAt,
    updatedBy: row.updatedBy != null ? String(row.updatedBy) : null,
  };
}
